import { useDidMount } from '@bygghemmait/hooks';
import { type RefObject, useState, useEffect } from 'react';

type State = 'entering' | 'exiting' | 'initial';

type UseEnter = {
  setState: (state: State) => void;
  state: State;
  nodeRef: RefObject<HTMLElement>;
};
const useEnter = ({ setState, state, nodeRef }: UseEnter) => {
  useEffect(() => {
    const node = nodeRef.current;
    if (!node || state !== 'entering') return;

    const resetState = () => setState('initial');

    node.addEventListener('animationend', resetState);
    return () => {
      node.removeEventListener('animationend', resetState);
    };
  }, [nodeRef, setState, state]);
};

type UseExit = Omit<UseEnter, 'setState'>
const useExit = ({ state, nodeRef: ref }: UseExit) => {
  const [isMounted, setMounted] = useState<boolean>(true)

  useEffect(() => {
    const node = ref.current;
    if (!node || state !== 'exiting') return;

    const unMount = () => setMounted(false);

    node.addEventListener('animationend', unMount);
    return () => {
      node.removeEventListener('animationend', unMount);
    };
  }, [ref, state]);

  return isMounted
};

type TransitionProps = {
  children: (state: State) => JSX.Element;
  playToast: () => void;
  isIn?: boolean;
  nodeRef: RefObject<HTMLElement>;
};
export const Transition = ({ children, playToast, isIn, nodeRef }: TransitionProps) => {
  const [state, setState] = useState<State>('entering');

  useDidMount(() => {
    playToast();
  });

  useEffect(() => {
    if (!isIn) {
      setState('exiting');
    }
  }, [isIn]);

  useEnter({ nodeRef, setState, state });
  const isMounted = useExit({ nodeRef, state });

  if (!isMounted) return null;

  return <>{children(state)}</>;
};
